import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  addingContactLinkInCandidateMaster,
  removeContactLinkInCandidateMaster,
  addPieplineContacts,
  updatePieplineContacts,
  deletePieplineContacts,
  updateContactLinkInCandidateMaster,
  indexMapping,
  ICandidate,
} from 'wisestep-api';
import { ControlledTextInput, Item, WsButton, WsDialog, WsTypoColors } from 'wisestep-base';
import { object, string } from 'yup';

import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { warning, success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';

interface IUpdateModal {
  id: string;
  open: boolean;
  candidate: ICandidate | null;
  onClose: () => void;
  actionAndValue: {
    action: string;
    value: string | null;
  };
}

const UpdateModal: FC<IUpdateModal> = ({ id, open, candidate, onClose, actionAndValue }) => {
  const refreshModal = useConversationWidgetStore((state) => state.updateId);
  const setRefreshSource = useConversationWidgetStore((state) => state.setRefreshSource);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const onWidgetClose = useConversationWidgetStore((state) => state.onWidgetClose);
  const formOptions = {
    resolver: yupResolver(
      object().shape({
        newData: string().required('Empty field detected'),
      })
    ),
  };
  const setNotification = useNotificationStore((state) => state.setNotification);
  const pipelineCandidateId = useConversationWidgetStore((state) => state.pipelineCandidateId);
  const [showCheckField, setShowCheckField] = useState(false);
  const methods = useForm(formOptions);
  const { handleSubmit, setValue } = methods;
  const [operation, setOperation] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [textLabel, setTextLabel] = useState<string>('');

  const setActionAndLabel = (action: string, label: string) => {
    setAction(action);
    setTextLabel(label);
  };

  useEffect(() => {
    switch (actionAndValue.action) {
      case 'addEmail':
        setShowCheckField(true);
        setOperation('add');
        setActionAndLabel('Email', 'Enter new email');
        break;
      case 'addPhone':
        setOperation('add');
        setActionAndLabel('Phone', 'Enter new phone number');
        break;
      case 'addLink':
        setOperation('add');
        setActionAndLabel('Profile URL', 'Enter new social link');
        break;
      case 'addTag':
        setOperation('add');
        setActionAndLabel('Tag', 'Enter new tag');
        break;
      case 'updateName':
        setOperation('add');
        setValue('newData', actionAndValue.value);
        setActionAndLabel('Name', 'Update Name');
        break;
      case 'deleteEmail':
        setOperation('delete');
        setActionAndLabel(
          'Email',
          `Are you sure you want to delete <br /> "${actionAndValue.value}" ?`
        );
        break;
      case 'deletePhone':
        setOperation('delete');
        setActionAndLabel(
          'Phone',
          `Are you sure you want to delete <br /> "${actionAndValue.value}" ?`
        );
        break;
      case 'deleteLink':
        setOperation('delete');
        setActionAndLabel(
          'Profile URL',
          `Are you sure you want to delete <br /> "${actionAndValue.value}" ?`
        );
        break;
      case 'deleteTag':
        setOperation('delete');
        setActionAndLabel(
          'Tag',
          `Are you sure you want to delete <br /> "${actionAndValue.value}" ?`
        );
        break;
    }
  }, [actionAndValue.action, actionAndValue.value, setValue]);

  const getKeyAccordingToAction = () => {
    switch (action) {
      case 'Email':
        return '0';
      case 'Phone':
        return '3';
      case 'Name':
        return '4';
      case 'Profile URL':
        return '1';
    }
    return null;
  };

  const refreshPage = () => {
    onClose();
    if (candidate?.id) {
      onWidgetClose();
      !pipelineCandidateId && updateFromPage('talentpool');
      setRefreshSource(true);
      refreshModal(candidate.id);
    }
  };

  const addNewField = async (data: string, isPrimaryEmail?: boolean) => {
    const pipelineUpdateKey = getKeyAccordingToAction();
    await addingContactLinkInCandidateMaster({
      id: id,
      newEmail: action === 'Email' ? data : null,
      newLink: action === 'Profile URL' ? data : null,
      newPhoneNumber: action === 'Phone' ? data : null,
      newTag: action === 'Tag' ? data : null,
      updatePrimaryEmail: isPrimaryEmail ?? false,
    });
    if (pipelineUpdateKey !== null && pipelineCandidateId) {
      await addPieplineContacts({
        key: pipelineUpdateKey,
        linkId: pipelineCandidateId,
        values: [data],
      });
    } else if (action === 'Tag' && pipelineCandidateId && candidate) {
      try {
        const prevTags = !isEmpty(candidate.tags) && candidate?.tags ? candidate.tags : [];
        prevTags.push(data);
        candidate.tags = prevTags;
        await indexMapping(candidate);
      } catch (e) {
        setNotification(failed('Something went wrong, Please try later !'));
      }
    }
    setNotification(success('New field added'));
    refreshPage();
  };

  const deleteNewField = async (data: string | null) => {
    const pipelineUpdateKey = getKeyAccordingToAction();
    if (data) {
      await removeContactLinkInCandidateMaster({
        id: id,
        oldEmail: action === 'Email' ? data : null,
        oldLink: action === 'Profile URL' ? data : null,
        oldPhoneNumber: action === 'Phone' ? data : null,
        oldTag: action === 'Tag' ? data : null,
        updatePrimaryEmail: false,
      });
      if (
        (action === 'Email' || action === 'Profile URL') &&
        pipelineUpdateKey !== null &&
        pipelineCandidateId
      ) {
        await deletePieplineContacts({
          key: pipelineUpdateKey,
          linkId: pipelineCandidateId,
          values: [data],
        });
      } else if (action === 'Tag' && pipelineCandidateId && candidate) {
        try {
          const prevTags = !isEmpty(candidate.tags) && candidate?.tags ? candidate.tags : [];
          const tags = prevTags.filter((tag) => tag !== data);
          candidate.tags = tags;
          await indexMapping(candidate);
        } catch (e) {
          setNotification(failed('Something went wrong, Please try later !'));
        }
      }
      setNotification(success('Field deleted'));
      refreshPage();
    }
  };

  const updateName = async (data: string) => {
    const pipelineUpdateKey = getKeyAccordingToAction();
    await updateContactLinkInCandidateMaster({
      id: id,
      newName: data,
    });
    if (pipelineUpdateKey !== null && pipelineCandidateId && actionAndValue.value) {
      await updatePieplineContacts({
        key: pipelineUpdateKey,
        linkId: pipelineCandidateId,
        newValue: data,
        oldValue: candidate?.name == actionAndValue.value ? actionAndValue.value : candidate?.name,
      });
    }
    setNotification(success('Name Updated'));
    refreshPage();
  };

  const onFormSubmit = async (data: FieldValues) => {
    switch (action) {
      case 'Email':
        if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(data.newData)) {
          await setTimeout(() => setValue('newData', ''), 200);
          setNotification(warning('Please enter valid email'));
          return null;
        }
        addNewField(data.newData, data.isPrimaryEmail);
        break;
      case 'Profile URL':
        if (
          !new RegExp(
            '((http|https)://)?(www.)?[a-zA-Z0-9@:%._\\+~#?&//=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%._\\+~#?&//=]*)'
          ).test(data.newData)
        ) {
          await setTimeout(() => setValue('newData', ''), 200);
          setNotification(warning('Please enter valid url'));
          return null;
        }
        addNewField(data.newData);
        break;
      case 'Phone':
        if (
          !/^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/.test(
            data.newData
          )
        ) {
          await setTimeout(() => setValue('newData', ''), 200);
          setNotification(warning('Please enter valid phone number'));
          return null;
        }
        addNewField(data.newData);
        break;
      case 'Tag':
        addNewField(data.newData);
        break;
      case 'Name':
        updateName(data.newData);
        break;
    }
  };

  return (
    <WsDialog openDialog={open}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container>
            <Grid item xs={12}>
              <Item>
                {operation === 'delete' ? (
                  <Typography
                    color={WsTypoColors.BLACK}
                    variant="h5"
                    dangerouslySetInnerHTML={{ __html: textLabel }}
                  />
                ) : (
                  <>
                    <ControlledTextInput
                      name="newData"
                      labelText={action}
                      type="text"
                      fullWidth
                      id="newData"
                      placeholder={textLabel}
                      variant="outlined"
                      autoComplete="marginTop"
                    />
                    {false && (
                      <>
                        {showCheckField}
                        <WsCheckBox name="isPrimaryEmail" label="Mark Email as Primary" />
                      </>
                    )}
                  </>
                )}
              </Item>
            </Grid>
          </Grid>
          <Grid container item xs={12} justifyContent="flex-end">
            <Item>
              <Stack direction="row" spacing={2}>
                {operation === 'delete' ? (
                  <>
                    <WsButton onClick={() => deleteNewField(actionAndValue.value)}>Yes</WsButton>
                    <WsButton variant="outlined" onClick={onClose}>
                      No
                    </WsButton>
                  </>
                ) : (
                  <>
                    <WsButton type="submit">Save</WsButton>
                    <WsButton variant="outlined" onClick={onClose}>
                      Close
                    </WsButton>
                  </>
                )}
              </Stack>
            </Item>
          </Grid>
        </form>
      </FormProvider>
    </WsDialog>
  );
};

export default UpdateModal;
